import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from '@mui/material';
import { reviewerStatementService } from '../../services/api/reviewerStatementService';
import { witnessStatementService } from '../../services/api/witnessStatementService';

const StatementReviewDialog = ({ open, onClose, statement, onReviewComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statementData, setStatementData] = useState(null);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [advokateErrors, setAdvokateErrors] = useState({});

  useEffect(() => {
    if (open && statement) {
      loadStatementContent();
    }
  }, [open, statement]);

  const loadStatementContent = async () => {
    try {
      setLoading(true);
      // Statement content is already loaded in the statement object
      if (statement.content) {
        setStatementData(statement.content);
      } else if (statement.draft_data) {
        setStatementData(statement.draft_data);
      }
    } catch (err) {
      console.error('Error loading statement content:', err);
      setError(err.message || 'Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build review updates
      const updates = {};

      if (reviewerNotes) {
        updates.reviewer_notes = reviewerNotes;
      }

      // Add ADVOKATE error_or_discrepancy fields if provided
      if (Object.keys(advokateErrors).length > 0) {
        updates.assertions = statementData.assertions.map((assertion, idx) => ({
          advokate: {
            error_or_discrepancy: advokateErrors[idx] || ''
          }
        }));
      }

      // Call reviewer API
      const response = await reviewerStatementService.reviewStatement(
        statement.statement_id,
        updates
      );

      onReviewComplete(response.statement);
      onClose();
    } catch (err) {
      console.error('Error reviewing statement:', err);
      setError(err.message || 'Failed to review statement');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!window.confirm('Are you sure you want to finalize this statement? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await reviewerStatementService.finalizeStatement(
        statement.statement_id
      );

      onReviewComplete(response.statement);
      onClose();
    } catch (err) {
      console.error('Error finalizing statement:', err);
      setError(err.message || 'Failed to finalize statement');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Please provide a reason for rejecting this statement:');
    if (!reason) return;

    try {
      setLoading(true);
      setError(null);

      const response = await reviewerStatementService.rejectStatement(
        statement.statement_id,
        { reason }
      );

      onReviewComplete(response.statement);
      onClose();
    } catch (err) {
      console.error('Error rejecting statement:', err);
      setError(err.message || 'Failed to reject statement');
    } finally {
      setLoading(false);
    }
  };

  if (!statement) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Review Witness Statement
        <Typography variant="body2" color="text.secondary">
          {statement.statement_id}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading && !statementData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Statement Metadata */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Statement Information
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Status:</TableCell>
                      <TableCell>
                        <Chip label={statement.status} size="small" color="info" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Created:</TableCell>
                      <TableCell>{new Date(statement.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                    {statement.submitted_at && (
                      <TableRow>
                        <TableCell>Submitted:</TableCell>
                        <TableCell>{new Date(statement.submitted_at).toLocaleString()}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>Witness:</TableCell>
                      <TableCell>{statement.witness_handle || statement.witness_email}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Narrative */}
            {statementData?.narrative && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Witness Narrative
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">
                    <strong>When:</strong> {statementData.narrative.stated_time}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Where:</strong> {statementData.narrative.stated_position}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {statementData.narrative.account}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Assertions */}
            {statementData?.assertions && statementData.assertions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Identification Assertions
                </Typography>
                {statementData.assertions.map((assertion, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Assertion {idx + 1}
                    </Typography>
                    {assertion.advokate && (
                      <Box>
                        <Typography variant="body2">
                          <strong>Time:</strong> {assertion.advokate.amount_of_time}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Distance:</strong> {assertion.advokate.distance}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Visibility:</strong> {assertion.advokate.visibility}
                        </Typography>

                        <TextField
                          fullWidth
                          label="Error or Discrepancy (Reviewer Only)"
                          value={advokateErrors[idx] || ''}
                          onChange={(e) => setAdvokateErrors(prev => ({
                            ...prev,
                            [idx]: e.target.value
                          }))}
                          margin="normal"
                          multiline
                          rows={2}
                          helperText="Note any errors or discrepancies in the identification"
                        />
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Reviewer Notes */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Reviewer Notes
              </Typography>
              <TextField
                fullWidth
                label="General Notes"
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                multiline
                rows={4}
                helperText="Add any additional notes about this statement"
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleReject}
          disabled={loading}
          color="error"
        >
          Reject
        </Button>
        <Button
          onClick={handleReview}
          disabled={loading}
          color="primary"
        >
          Save Review
        </Button>
        <Button
          onClick={handleFinalize}
          disabled={loading}
          variant="contained"
          color="success"
        >
          Finalize
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(StatementReviewDialog);
